import React, { createContext, useContext, useState, ReactNode, useRef } from 'react';
import { Toast, ToastType } from '../components/ui/Toast';

interface ToastContextProps {
  showToast: (message: string, type?: ToastType, options?: { title?: string; avatarUrl?: string; duration?: number; key?: string }) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  title?: string;
  avatarUrl?: string;
  duration?: number;
  count?: number;
  key?: string;
}

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idCounterRef = useRef(0);

  const showToast = (message: string, type: ToastType = 'info', options?: { title?: string; avatarUrl?: string; duration?: number; key?: string }) => {
    // Combine timestamp with a monotonically increasing counter to avoid duplicate keys
    idCounterRef.current += 1;
    const id = `toast-${Date.now()}-${idCounterRef.current}`;
    if (options?.key) {
      setToasts(prev => {
        const filtered = prev.filter(t => t.key !== options.key);
        const nextList = [...filtered, { id, message, type, ...options, count: 1 } as Toast];
        return nextList.slice(-2);
      });
    } else {
      setToasts(prev => {
        const nextList = [...prev, { id, message, type, ...options }];
        return nextList.slice(-2);
      });
    }
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-36 sm:top-40 right-4 sm:right-6 z-[99999] flex flex-col gap-2.5 items-end pointer-events-none max-w-[calc(100vw-2rem)] sm:max-w-md">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              message={toast.message}
              type={toast.type}
              title={toast.title}
              avatarUrl={toast.avatarUrl}
              duration={toast.duration || 3000}
              count={toast.count}
              onClose={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
